import { createContext, useEffect, useState } from "react";
import { BookText } from "lucide-react";
import type { ShownTerms, TermsResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { useTerms } from "@/hooks/use-terms";
import type { SubStepId, V2StageId } from "@/v2/stage-vocabulary";
import {
  changedSince,
  readSeen,
  seenOf,
  shownTermsOf,
  writeSeen,
  type SeenTerms,
} from "./terms-seen";
import {
  LeaveQuestion,
  ReturnBar,
  useReturnTrip,
  type ReturnTrip,
} from "./ReturnTrip";
import { type GoToSource } from "./TermEntries";
import { TermsPanel } from "./TermsPanel";

/** Where the toggle stands on a page: not drawn, drawn and greyed, or offered. */
export type TerminologyPlace = "hidden" | "greyed" | "active";

/** The stages whose pages offer the panel, from Classification to Specification. */
const OFFERED: ReadonlySet<V2StageId> = new Set([
  "intake",
  "figures",
  "thesis",
  "claim-strategy",
  "planning-outline",
]);

/**
 * Greyed on Upload Materials, where the inputs are still being added; offered
 * from Classification to Specification; hidden on Generate, whose review shows
 * the terms itself, and off a draft's stages.
 */
export function terminologyPlace(
  stageId: V2StageId | undefined,
  subStepId: SubStepId | undefined,
): TerminologyPlace {
  if (stageId === undefined || !OFFERED.has(stageId)) return "hidden";
  return stageId === "intake" && subStepId === "materials"
    ? "greyed"
    : "active";
}

export interface Terminology {
  place: TerminologyPlace;
  open: boolean;
  /** Open and on a page that offers it: the dock is drawn. */
  shown: boolean;
  toggle: () => void;
  close: () => void;
  view: TermsResponse | undefined;
  /** What the attorney saw at the opening before this one, which the flags compare against. */
  seen: SeenTerms | null;
  /** A term moved since the attorney last looked: the dot on the closed toggle. */
  changed: boolean;
  /** Open a term's record on the page that defines it. */
  go: GoToSource;
  /** The way back from a term's source: the bar and the question before leaving. */
  trip: ReturnTrip;
  /** The terms as Generate's review showed them, which the start sends for the lock to count edits against. */
  review: ReviewShown;
}

/** The terms the review showed when the attorney came to it, kept across trips to edit them. */
export interface ReviewShown {
  shown: ShownTerms | null;
  /**
   * The review showing `view`. A review come to afresh shows its baseline anew;
   * one returned to from a trip keeps the one it showed before the trip.
   */
  note: (view: TermsResponse, fresh: boolean) => void;
}

/** The shell's Terminology state, for a page that lists the terms itself, as Generate's review does. */
export const TerminologyContext = createContext<Terminology | null>(null);

function useReviewShown(taskId: string): ReviewShown {
  const [held, setHeld] = useState<{
    taskId: string;
    shown: ShownTerms;
  } | null>(null);
  return {
    shown: held?.taskId === taskId ? held.shown : null,
    note: (view, fresh) => {
      if (fresh || held?.taskId !== taskId) {
        setHeld({ taskId, shown: shownTermsOf(view) });
      }
    },
  };
}

/**
 * The panel's state for the shell: whether it is open, the terms, and what the
 * attorney saw when they last opened it.
 *
 * Opening reads the stored record as the baseline its flags compare against.
 * While the panel is shown, every view it shows is recorded as seen, so a term
 * renamed with the panel open is not flagged again once it is closed. The
 * baseline belongs to the draft it was read for; on another draft nothing is
 * flagged until the panel is opened there.
 *
 * `here` names the page the attorney is on, for the Return bar.
 */
export function useTerminology(
  taskId: string | undefined,
  place: TerminologyPlace,
  here: string,
): Terminology {
  const id = taskId ?? "";
  const offered = place === "active" && taskId !== undefined;
  const terms = useTerms(id, offered);
  const view = terms.data;
  const trip = useReturnTrip(id, view, here);
  const review = useReviewShown(id);
  const [opened, setOpened] = useState<{
    taskId: string;
    seen: SeenTerms | null;
  } | null>(null);
  const open = opened !== null;
  const shown = open && offered;

  useEffect(() => {
    if (shown && view !== undefined) writeSeen(id, seenOf(view));
  }, [shown, view, id]);

  const close = () => {
    setOpened(null);
  };
  return {
    place,
    open,
    shown,
    toggle: () => {
      if (open) close();
      else setOpened({ taskId: id, seen: readSeen(id) });
    },
    close,
    view,
    seen: opened?.taskId === id ? opened.seen : null,
    go: trip.go,
    trip,
    review,
    changed:
      !shown &&
      offered &&
      view !== undefined &&
      changedSince(view, readSeen(id)),
  };
}

const GREYED_TITLE =
  "Available from Classification, once your materials are uploaded";

function toggleTitle(terminology: Terminology): string {
  if (terminology.place === "greyed") return GREYED_TITLE;
  if (terminology.changed)
    return "Terms have changed since you last opened this table";
  return terminology.shown
    ? "Hide the terminology table"
    : "Show the terminology table";
}

/** The header's Terminology toggle, with a dot when a term moved since the panel was last opened. */
export function TerminologyToggle({
  terminology,
}: {
  terminology: Terminology;
}) {
  if (terminology.place === "hidden") return null;
  const greyed = terminology.place === "greyed";
  const button = (
    <Button
      variant="shell"
      size="pill"
      aria-pressed={terminology.shown}
      disabled={greyed}
      title={toggleTitle(terminology)}
      onClick={terminology.toggle}
    >
      <BookText aria-hidden />
      Terminology
      {terminology.changed && (
        <>
          <span
            aria-hidden
            className="-ml-px size-[7px] rounded-full bg-accent"
          />
          <span className="sr-only">, terms changed</span>
        </>
      )}
    </Button>
  );
  // A disabled button takes no pointer, so its own title never shows; the
  // wrapper holds it for the pointer, saying when the panel becomes available.
  return greyed ? <span title={GREYED_TITLE}>{button}</span> : button;
}

/**
 * The right-hand column the panel is drawn in while it is shown: a track of the
 * shell's grid where the window is wide enough, and over the content from the
 * right, under the header, where it is not.
 */
export function TerminologyDock({ terminology }: { terminology: Terminology }) {
  if (!terminology.shown) return null;
  return (
    <aside className="fixed top-[var(--shell-header-h)] right-0 bottom-0 z-60 w-[min(360px,92vw)] overflow-hidden border-l border-border bg-background shadow-[-8px_0_30px_rgba(15,0,27,0.14)] dock:static dock:z-auto dock:min-h-0 dock:w-auto dock:shadow-none">
      <TermsPanel
        view={terminology.view}
        seen={terminology.seen}
        onClose={terminology.close}
        onGo={terminology.go}
        pending={terminology.trip.opened}
      />
    </aside>
  );
}

/**
 * The bar that brings the attorney back from a term's source, on the pages that
 * offer the panel, and the question before a panel move would drop a draft.
 */
export function TerminologyTrip({ terminology }: { terminology: Terminology }) {
  return (
    <>
      {terminology.place === "active" && (
        <ReturnBar trip={terminology.trip} docked={terminology.shown} />
      )}
      <LeaveQuestion trip={terminology.trip} />
    </>
  );
}
